import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/util/cn';

// Single resort tile: 16:10 cover photo with the resort name + region
// overlaid bottom-left on a brand gradient scrim (navy in winter, summer
// green in summer mode) so the labels stay legible over any photo. Subtle
// hover zoom; the whole card is the click target. Used by NearbyResorts
// (carousel) and standalone surfaces.

export type NearbyResort = {
  name: string;
  /** Region or country label shown in the subtitle. */
  region: string;
  /** Optional distance string, e.g. "15 mi". When set, subtitle reads
   *  "region · distance"; otherwise just the region. */
  distance?: string;
  /** Image URL. */
  img: string;
  href?: string;
};

export type NearbyResortCardProps = {
  resort: NearbyResort;
  /** Swaps the winter navy scrim + subtitle for the summer-green palette. */
  isSummerMode?: boolean;
  className?: string;
};

export const NearbyResortCard = ({ resort, isSummerMode, className }: NearbyResortCardProps) => (
  <Link
    href={resort.href ?? '#'}
    className={cn(
      'group relative block aspect-[16/10] overflow-hidden rounded-md shadow-sm',
      isSummerMode ? 'bg-primary-summer' : 'bg-dark-blue',
      className,
    )}
  >
    <img
      src={resort.img}
      alt={resort.name}
      loading="lazy"
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
    />
    {/* Bottom-up brand scrim — navy (winter) / summer green — keeps the
        overlaid name + region legible over any photo. */}
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 bg-gradient-to-t',
        isSummerMode
          ? 'from-primary-summer/95 via-primary-summer/20 to-transparent'
          : 'from-dark-blue/95 via-dark-blue/20 to-transparent',
      )}
    />
    <div className="absolute inset-x-0 bottom-0 p-3">
      <div className="text-sm font-bold leading-none text-white underline-offset-2 group-hover:underline">
        {resort.name}
      </div>
      <div className={cn('mt-0.5 text-xs', isSummerMode ? 'text-emerald-100' : 'text-blue-200')}>
        {resort.region}
        {resort.distance && (
          <>
            {' · '}
            {resort.distance}
          </>
        )}
      </div>
    </div>
  </Link>
);
